import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from './CookieConsentContext';
import '../styles/cookie-banner.css';

function Toggle({ checked, onChange, disabled }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			className={`cb-toggle ${checked ? 'cb-toggle-on' : ''} ${disabled ? 'cb-toggle-disabled' : ''}`}
			onClick={() => !disabled && onChange(!checked)}
			disabled={disabled}
		>
			<span className="cb-toggle-knob" />
		</button>
	);
}

function PreferencesModal({ onClose }) {
	const { t } = useTranslation();
	const { consent, savePreferences } = useCookieConsent();
	const [analytics, setAnalytics] = useState(consent.analytics);
	const [marketing, setMarketing] = useState(consent.marketing);

	const handleSave = () => {
		savePreferences({ analytics, marketing });
		onClose();
	};

	return (
		<div className="cb-overlay" onClick={onClose}>
			<div className="cb-modal" onClick={(e) => e.stopPropagation()}>
				<div className="cb-modal-header">
					<h3 className="cb-modal-title">{t('cookies.preferencesTitle')}</h3>
					<button className="cb-modal-close" onClick={onClose} aria-label={t('cookies.close')}>&times;</button>
				</div>

				<div className="cb-modal-body">
					<p className="cb-modal-desc">
						{t('cookies.preferencesDesc')}{' '}
						<a href="/cookie-policy">Cookie Policy</a>.
					</p>

					<div className="cb-category">
						<div className="cb-category-header">
							<div>
								<strong>{t('cookies.necessary')}</strong>
								<p className="cb-category-desc">{t('cookies.necessaryDesc')}</p>
							</div>
							<Toggle checked={true} onChange={() => {}} disabled={true} />
						</div>
					</div>

					<div className="cb-category">
						<div className="cb-category-header">
							<div>
								<strong>{t('cookies.analytics')}</strong>
								<p className="cb-category-desc">{t('cookies.analyticsDesc')}</p>
							</div>
							<Toggle checked={analytics} onChange={setAnalytics} />
						</div>
					</div>

					<div className="cb-category">
						<div className="cb-category-header">
							<div>
								<strong>{t('cookies.marketing')}</strong>
								<p className="cb-category-desc">{t('cookies.marketingDesc')}</p>
							</div>
							<Toggle checked={marketing} onChange={setMarketing} />
						</div>
					</div>
				</div>

				<div className="cb-modal-footer">
					<button className="cb-btn cb-btn-secondary" onClick={onClose}>{t('cookies.cancel')}</button>
					<button className="cb-btn cb-btn-primary" onClick={handleSave}>{t('cookies.savePreferences')}</button>
				</div>
			</div>
		</div>
	);
}

export default function CookieBanner() {
	const { t } = useTranslation();
	const { showBanner, showPreferences, setShowPreferences, acceptAll, rejectAll, consentGiven } = useCookieConsent();

	return (
		<>
			{/* Banner */}
			{showBanner && (
				<div className="cb-banner">
					<div className="cb-banner-content">
						<div className="cb-banner-text">
							<strong>{t('cookies.bannerTitle')}</strong>
							<p>
								{t('cookies.bannerText')}{' '}
								<a href="/cookie-policy">Cookie Policy</a>
							</p>
						</div>
						<div className="cb-banner-actions">
							<button className="cb-btn cb-btn-secondary" onClick={rejectAll}>
								{t('cookies.rejectAll')}
							</button>
							<button className="cb-btn cb-btn-outline" onClick={() => setShowPreferences(true)}>
								{t('cookies.customize')}
							</button>
							<button className="cb-btn cb-btn-primary" onClick={acceptAll}>
								{t('cookies.acceptAll')}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Floating button */}
			{consentGiven && !showBanner && !showPreferences && (
				<button
					className="cb-floating-btn"
					onClick={() => setShowPreferences(true)}
					aria-label={t('cookies.managePreferences')}
					title={t('cookies.managePreferences')}
				>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
					</svg>
				</button>
			)}

			{/* Preferences modal */}
			{showPreferences && <PreferencesModal onClose={() => setShowPreferences(false)} />}
		</>
	);
}
